import {
  BatteryChargingIcon,
  DropletsIcon,
  FlameIcon,
  GraduationCapIcon,
  LeafIcon,
  SlidersHorizontalIcon,
  SproutIcon,
  SunIcon,
  WindIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  classroom: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Renewable Energy Fundamentals & the Global Energy Transition',
    subtitle: 'The foundation your students need to understand everything else',
    classroom:
      'Your first introduction to renewable energy can be transformative or tedious. This volume transforms technical concepts into something comprehensible and relevant from day one.',
    items: [
      'What is renewable energy and why it matters',
      'Renewable vs. non-renewable energy sources',
      'Global energy systems and matrices',
      'Sustainability and climate change',
      'Energy efficiency and the energy transition',
      'Clear explanations with real-world examples',
    ],
    result:
      'Your students understand renewable energy as a practical, urgent solution — not an impossible list of terms to memorize.',
    icon: LeafIcon,
  },
  {
    number: '2',
    title: 'Solar Energy: Photovoltaic Systems & Applications',
    subtitle: 'Everything your students need to know about the most accessible technology',
    classroom:
      'This is where many students struggle. This volume gives you the materials to help them truly understand how solar panels and complete systems work.',
    items: [
      'Solar radiation fundamentals and the photovoltaic effect',
      'Solar panel types, characteristics, and efficiency',
      'Inverters, controllers, and charge regulators',
      'Battery storage and energy management',
      'Electrical connections and safety systems',
      'Off-grid and grid-connected systems',
      'Basic system sizing and dimensioning',
      '15 worked calculation examples',
    ],
    result: 'Your students learn to design solar installations with method and confidence.',
    icon: SunIcon,
  },
  {
    number: '3',
    title: 'Wind Energy: From Theory to Real-World Applications',
    subtitle: 'How to teach wind energy clearly and practically',
    classroom:
      'Wind energy is fascinating but complex. This volume gives you everything needed so your students understand how wind becomes electricity.',
    items: [
      'Wind energy fundamentals',
      'Wind characteristics and average velocity',
      'Wind turbine components and operation',
      'Power curves and efficiency',
      'Wind farms and small-scale systems',
      'Installation and maintenance considerations',
      '8 practical case studies ready for classroom use',
      'Power calculation exercises',
    ],
    result: 'Your students understand why wind is such a valuable energy source globally.',
    icon: WindIcon,
  },
  {
    number: '4',
    title: 'Hydropower & Marine Energy: Water-Based Renewable Systems',
    subtitle: 'Water-based energy explained comprehensively',
    classroom: 'Hydroelectricity is complex but fundamental. This volume breaks it down without losing clarity.',
    items: [
      'Hydroelectric generation fundamentals',
      'Water flow, head, and power relationships',
      'Hydraulic turbines and plant types',
      'Large-scale hydroelectric facilities',
      'Small-scale and micro-hydropower systems',
      'Advantages, limitations, and environmental impacts',
      'Global hydropower examples',
      '10 practical application case studies',
    ],
    result:
      'Your students understand why hydropower is so important globally and recognize real-world challenges.',
    icon: DropletsIcon,
  },
  {
    number: '5',
    title: 'Biomass, Bioenergy & Biofuels: Organic Energy Resources',
    subtitle: 'Converting waste and local resources into renewable energy',
    classroom:
      'Biomass and biogas often get left out of curricula. This volume gives you the confidence to include them.',
    items: [
      'Biomass energy fundamentals',
      'Agricultural, forestry, and waste biomass sources',
      'Solid biomass characteristics and applications',
      'Anaerobic digesters and biogas production',
      'Bioethanol and biodiesel production',
      'Energy conversion processes',
      'Practical applications in rural communities',
      '6 biogas system design projects',
    ],
    result: 'Your students see renewable energy opportunities in their own local resources.',
    icon: SproutIcon,
  },
  {
    number: '6',
    title: 'Geothermal Energy & Emerging Renewable Technologies',
    subtitle: 'Beyond the familiar: advanced and emerging renewable solutions',
    classroom:
      'Geothermal, marine, and emerging systems complete the renewable energy picture. This volume closes the gap.',
    items: [
      'Geothermal energy fundamentals',
      'Geothermal power plant types',
      'Heating and cooling applications',
      'Tidal and wave energy systems',
      'Ocean current energy',
      'Green hydrogen production',
      'Emerging renewable technologies',
      '5 real-world case studies',
    ],
    result: 'Your students have a complete understanding of the full renewable energy spectrum.',
    icon: FlameIcon,
  },
  {
    number: '7',
    title: 'Energy Storage, Smart Grids & System Integration',
    subtitle: 'How renewable electricity is stored, managed, and distributed',
    classroom:
      'Energy storage is critical. This volume clearly explains batteries, storage systems, and smart grid integration.',
    items: [
      'Battery types and technologies',
      'Battery capacity, power, and charge cycles',
      'System autonomy and selection criteria',
      'Battery Energy Storage Systems (BESS)',
      'Hybrid systems: solar + wind + storage',
      'Microgrids and intelligent energy management',
      'Solar-wind-battery combinations',
      'Autonomy calculations',
      '12 practical hybrid system case studies',
    ],
    result: 'Your students understand why storage is the key to making renewables work in the real world.',
    icon: BatteryChargingIcon,
  },
  {
    number: '8',
    title: 'Designing & Sizing Renewable Energy Systems',
    subtitle: 'The most technically applied volume',
    classroom:
      'This is the practical heart of the collection. Your students learn to design real systems from the ground up.',
    items: [
      'Energy consumption and demand calculations',
      'Climate and solar data interpretation',
      'Solar and wind generation estimation',
      'Component selection by need',
      'Panel, inverter, and battery sizing',
      'Loss and efficiency calculations',
      'Autonomy and selection criteria',
      'Technical alternative comparisons',
      '20 step-by-step worked exercises',
      '8 complete system design case studies',
    ],
    result: 'Your students graduate prepared to design actual renewable energy installations.',
    icon: SlidersHorizontalIcon,
  },
  {
    number: '9',
    title: 'Teaching Renewable Energy: Pedagogy & 2026 Curriculum Planning',
    subtitle: 'Your complete guide as an educator',
    classroom:
      'This volume gives you exactly what you need to plan, organize, and evaluate your entire year.',
    items: [
      'Current approaches to teaching renewable energy',
      'Progressive content organization',
      'Suggested annual planning framework',
      'Complete teaching sequences',
      'Structured classroom and lab activities',
      'Project-based learning strategies',
      'Digital tools and simulation integration',
      'Working with limited resources',
      'Practical and theoretical assessment',
      'Rubrics and evaluation criteria',
      '10 integrated capstone projects',
    ],
    result: 'You\'ll never wonder "what do I teach today?" again. Everything is planned and contextualized.',
    icon: GraduationCapIcon,
  },
];
